import type { ComponentType } from "react";
import type { IconProps } from "@phosphor-icons/react";
import {
  AndroidLogo as PhAndroidLogo,
  AppleLogo as PhAppleLogo,
  ArrowLeft as PhArrowLeft,
  ArrowRight as PhArrowRight,
  ArrowsHorizontal as PhArrowsHorizontal,
  ArrowSquareOut as PhArrowSquareOut,
  ArrowsClockwise as PhArrowsClockwise,
  ArrowsCounterClockwise as PhArrowsCounterClockwise,
  Bluetooth as PhBluetooth,
  BluetoothX as PhBluetoothX,
  BuildingOffice as PhBuildingOffice,
  BugBeetle as PhBugBeetle,
  CaretDown as PhCaretDown,
  CaretRight as PhCaretRight,
  ChartLine as PhChartLine,
  Check as PhCheck,
  CheckCircle as PhCheckCircle,
  Circle as PhCircle,
  Clock as PhClock,
  ClockCountdown as PhClockCountdown,
  Copy as PhCopy,
  CornersOut as PhCornersOut,
  Database as PhDatabase,
  DotsThreeOutline as PhDotsThreeOutline,
  DownloadSimple as PhDownloadSimple,
  Eye as PhEye,
  Flask as PhFlask,
  Funnel as PhFunnel,
  GearSix as PhGearSix,
  GoogleLogo as PhGoogleLogo,
  House as PhHouse,
  Info as PhInfo,
  Layout as PhLayout,
  Laptop as PhLaptop,
  ListBullets as PhListBullets,
  LockSimple as PhLockSimple,
  LockSimpleOpen as PhLockSimpleOpen,
  MagnifyingGlass as PhMagnifyingGlass,
  Moon as PhMoon,
  MusicNotes as PhMusicNotes,
  Pause as PhPause,
  PencilSimple as PhPencilSimple,
  Play as PhPlay,
  Playlist as PhPlaylist,
  Plus as PhPlus,
  Prohibit as PhProhibit,
  Queue as PhQueue,
  ShieldCheck as PhShieldCheck,
  Shuffle as PhShuffle,
  SidebarSimple as PhSidebarSimple,
  SignOut as PhSignOut,
  SkipBack as PhSkipBack,
  SkipForward as PhSkipForward,
  Skull as PhSkull,
  SlackLogo as PhSlackLogo,
  SpotifyLogo as PhSpotifyLogo,
  SpeakerHigh as PhSpeakerHigh,
  SpeakerSlash as PhSpeakerSlash,
  SpinnerGap as PhSpinnerGap,
  StopCircle as PhStopCircle,
  Timer as PhTimer,
  Trash as PhTrash,
  Sun as PhSun,
  User as PhUser,
  UserCircle as PhUserCircle,
  Users as PhUsers,
  WarningCircle as PhWarningCircle,
  WarningDiamond as PhWarningDiamond,
  Waveform as PhWaveform,
  X as PhX,
  YoutubeLogo as PhYoutubeLogo,
} from "@phosphor-icons/react";

type AppIconComponent = ComponentType<IconProps>;

function withIconDefaults(
  Icon: AppIconComponent,
  defaultWeight: IconProps["weight"] = "regular"
) {
  function WrappedIcon({ weight, ...props }: IconProps) {
    return <Icon weight={weight ?? defaultWeight} {...props} />;
  }

  WrappedIcon.displayName = `Icon(${Icon.displayName ?? Icon.name ?? "Phosphor"})`;
  return WrappedIcon;
}

export const Activity = withIconDefaults(PhWaveform);
export const AndroidLogo = withIconDefaults(PhAndroidLogo, "fill");
export const AlertCircle = withIconDefaults(PhWarningCircle);
export const AppleLogo = withIconDefaults(PhAppleLogo, "fill");
export const ArrowLeft = withIconDefaults(PhArrowLeft);
export const ArrowRight = withIconDefaults(PhArrowRight);
export const ArrowsHorizontal = withIconDefaults(PhArrowsHorizontal);
export const Bluetooth = withIconDefaults(PhBluetooth);
export const BluetoothOff = withIconDefaults(PhBluetoothX);
export const BuildingOffice = withIconDefaults(PhBuildingOffice);
export const BugBeetle = withIconDefaults(PhBugBeetle);
export const Check = withIconDefaults(PhCheck);
export const CheckCircle = withIconDefaults(PhCheckCircle);
export const CheckIcon = withIconDefaults(PhCheck);
export const ChevronDown = withIconDefaults(PhCaretDown);
export const ChevronRightIcon = withIconDefaults(PhCaretRight);
export const CircleIcon = withIconDefaults(PhCircle, "fill");
export const CircleOff = withIconDefaults(PhProhibit);
export const Clock = withIconDefaults(PhClock);
export const ClockCountdown = withIconDefaults(PhClockCountdown);
export const Copy = withIconDefaults(PhCopy);
export const CornersOut = withIconDefaults(PhCornersOut);
export const Database = withIconDefaults(PhDatabase);
export const Download = withIconDefaults(PhDownloadSimple);
export const Eye = withIconDefaults(PhEye);
export const ExternalLink = withIconDefaults(PhArrowSquareOut);
export const Filter = withIconDefaults(PhFunnel);
export const FlaskConical = withIconDefaults(PhFlask);
export const GoogleLogo = withIconDefaults(PhGoogleLogo, "fill");
export const House = withIconDefaults(PhHouse);
export const Info = withIconDefaults(PhInfo);
export const Layout = withIconDefaults(PhLayout);
export const Laptop = withIconDefaults(PhLaptop);
export const LineChart = withIconDefaults(PhChartLine);
export const ListBullets = withIconDefaults(PhListBullets);
export const ListMusic = withIconDefaults(PhPlaylist);
export const Loader2 = withIconDefaults(PhSpinnerGap);
export const Lock = withIconDefaults(PhLockSimple);
export const LockOpen = withIconDefaults(PhLockSimpleOpen);
export const LogOut = withIconDefaults(PhSignOut);
export const MoreHorizontal = withIconDefaults(PhDotsThreeOutline);
export const Moon = withIconDefaults(PhMoon);
export const Music2 = withIconDefaults(PhMusicNotes);
export const Pause = withIconDefaults(PhPause);
export const Pencil = withIconDefaults(PhPencilSimple);
export const Play = withIconDefaults(PhPlay);
export const Plus = withIconDefaults(PhPlus);
export const Queue = withIconDefaults(PhQueue);
export const RefreshCcw = withIconDefaults(PhArrowsCounterClockwise);
export const RefreshCw = withIconDefaults(PhArrowsClockwise);
export const Search = withIconDefaults(PhMagnifyingGlass);
export const Settings = withIconDefaults(PhGearSix);
export const ShieldCheck = withIconDefaults(PhShieldCheck);
export const Shuffle = withIconDefaults(PhShuffle);
export const SidebarSimple = withIconDefaults(PhSidebarSimple);
export const SkipBack = withIconDefaults(PhSkipBack);
export const SkipForward = withIconDefaults(PhSkipForward);
export const Skull = withIconDefaults(PhSkull);
export const SlackLogo = withIconDefaults(PhSlackLogo, "fill");
export const SpotifyLogo = withIconDefaults(PhSpotifyLogo, "fill");
export const SpeakerHigh = withIconDefaults(PhSpeakerHigh);
export const SpeakerSlash = withIconDefaults(PhSpeakerSlash);
export const StopCircle = withIconDefaults(PhStopCircle);
export const Timer = withIconDefaults(PhTimer);
export const Trash2 = withIconDefaults(PhTrash);
export const Sun = withIconDefaults(PhSun);
export const TriangleAlert = withIconDefaults(PhWarningDiamond);
export const User = withIconDefaults(PhUser);
export const UserRound = withIconDefaults(PhUserCircle);
export const Users = withIconDefaults(PhUsers);
export const X = withIconDefaults(PhX);
export const XIcon = withIconDefaults(PhX);
export const YoutubeLogo = withIconDefaults(PhYoutubeLogo, "fill");
